import type {
  ComponentType,
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren
} from 'react';
import { useContext } from 'react';
import styled from 'styled-components';
import { Icon } from '@elfsight/lib-icon';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import tinycolor from 'tinycolor2';
import { DEFAULT_ALPHA_BACKGROUND_ITEM } from './constants';
import { DropdownContext } from './DropdownContext';
import { getAlphaForBackground } from './getAlphaForBackground';
import type { IconSize } from './types';

export type DropdownItemProps = PropsWithChildren<{
  disabled?: boolean;
  selected?: boolean;
  alphaBackground?: number;
  fontColor?: string;
  icon?: ComponentType;
  iconLeft?: ComponentType;
  onClick?(e: MouseEvent | KeyboardEvent): void;
  ariaLabel?: string;
  itemStyle?: CSSProperties;
}>;

export function DropdownItem({
  children,
  alphaBackground = DEFAULT_ALPHA_BACKGROUND_ITEM,
  fontColor,
  icon,
  iconLeft,
  onClick,
  selected,
  disabled,
  ariaLabel,
  itemStyle,
  ...forwardingProps
}: DropdownItemProps) {
  const {
    background,
    mainFontColor,
    closeOnClick,
    withoutIcons,
    iconSize,
    onCloseRequest
  } = useContext(DropdownContext);

  const handleSelect = (e: MouseEvent | KeyboardEvent) => {
    if (onClick) {
      onClick(e);
    }
    if (closeOnClick) {
      onCloseRequest();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSelect(e);
    }
  };

  const currentFontColor = fontColor || mainFontColor;
  const disabledFontColor = tinycolor(fontColor || mainFontColor)
    .clone()
    .setAlpha(0.45)
    .toString();

  return (
    <ItemContainer
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      tabIndex={0}
      role="option"
      aria-selected={selected}
      _disabled={disabled}
      _selected={selected}
      _fontColor={currentFontColor}
      _alphaBackground={getAlphaForBackground(background, alphaBackground)}
      {...forwardingProps}
    >
      {iconLeft && (
        <ItemIcon
          component={iconLeft}
          size={iconSize}
          fill={disabled ? disabledFontColor : currentFontColor}
        />
      )}

      <Item
        style={itemStyle}
        _fontColor={disabled ? disabledFontColor : currentFontColor}
        _withoutIcon={withoutIcons}
        _iconSize={iconSize}
        _icon={icon}
      >
        {children}
      </Item>

      {icon && (
        <ItemIcon
          component={icon}
          size={iconSize}
          fill={disabled ? disabledFontColor : currentFontColor}
        />
      )}
    </ItemContainer>
  );
}

const MARGIN_ICON = 12;

export const ItemContainer = styled.div<{
  _alphaBackground: number;
  _fontColor?: string;
  _disabled?: boolean;
  _selected?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  cursor: ${({ _disabled }) => (_disabled ? 'default' : 'pointer')};
  pointer-events: ${({ _disabled }) => (_disabled ? 'none' : undefined)};
  gap: 12px;

  background: ${({ _selected, _fontColor, _alphaBackground }) =>
    _selected
      ? _fontColor && getColorWithAlpha(_fontColor, _alphaBackground + 0.05)
      : 'none'};

  &:hover,
  &:focus {
    background: ${({ _fontColor, _selected, _alphaBackground }) =>
      _fontColor &&
      !_selected &&
      getColorWithAlpha(_fontColor, _alphaBackground)};
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const Item = styled.div<{
  _fontColor: string;
  _withoutIcon: boolean;
  _iconSize: IconSize;
  _icon?: ComponentType;
}>`
  width: 100%;
  align-self: center;
  color: ${({ _fontColor }) => _fontColor};
  margin-right: ${({ _withoutIcon, _icon, _iconSize }) => {
    if (_withoutIcon) {
      return 0;
    }

    if (_icon) {
      return MARGIN_ICON;
    }

    return MARGIN_ICON + _iconSize;
  }}px;
`;

export const ItemIcon = styled(Icon)``;
